import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '@api/Firebase/Firebase';
import { ProfileTypes, UserType } from '@api/Users/User';
import UserServ, { PersonalDataType, UpdatePinType } from '@api/Users/UserServ';
import { axiosPrivate } from '@api/ApiHelper/axiosPrivate';
import ActivityLogServ from '@api/ActivityLogs/ActivityLogServ';
import { describeUserEmailChange, describeUserPinReset } from '@api/ActivityLogs/ConfigActivity';
import { invalidateUserCache } from '@api/Users/UserServ';
import { BranchOffice } from '@api/BranchOffices/BranchOffice';
import { canCurrentUser, getCurrentPermissionSubject } from '@utils/Permissions';
import { canManageUser, Capability } from '@utils/Permissions/Capabilities';

export interface SignupFormValues {
    uid?: string;
    firstname: string;
    lastname: string;
    email: string;
    phone: string;
    profile: string;
    branchOffices: string[];
    address: string;
    password: string;
}

export interface LoginFormValues {
    email: string;
    password: string;
}

/** El server dice qué pasó (`{ error }` con su status); axios solo trae "Request failed". */
const serverError = (error: any): any =>
    error?.response?.data?.error ? new Error(error.response.data.error) : error;

class UserService {
    /**
     * Alta o edición. Devuelve el usuario como quedó en el server: en un alta sin
     * correo, ahí viene el correo interno con el que va a entrar (§8.3 del plan de
     * Gastos), y la pantalla se lo tiene que decir a quien lo dio de alta.
     */
    onCreateUser = (signupFormValues: SignupFormValues): Promise<UserType> => {
        return new Promise(async (resolve, reject) => {
            try {
                await UserServ.assertCanSaveUser(signupFormValues);
                const { data } = await axiosPrivate.post('/users', signupFormValues);
                if (data?.uid) invalidateUserCache(data.uid);
                resolve(data as UserType);
            } catch (error: any) {
                reject(serverError(error));
            }
        });
    };

    /**
     * Cambia con qué correo entra un usuario (el server mueve Firebase Auth y el
     * doc, en ese orden). Es control de acceso: deja renglón en la bitácora del
     * usuario con el correo de antes y el nuevo.
     */
    onUpdateEmail = async (user: Pick<UserType, 'uid' | 'profile'>, email: string): Promise<string> => {
        try {
            // El propio usuario (Mi perfil) o quien lo administra (Usuarios).
            if (user.uid !== UserServ.getUserId() && !canManageUser(getCurrentPermissionSubject(), user.profile)) {
                throw new Error('Este usuario no es de tus sucursales');
            }
            const { data } = await axiosPrivate.post(`/users/${user.uid}/email`, { email });
            invalidateUserCache(user.uid);
            if (data?.previousEmail !== data?.email) {
                try {
                    await ActivityLogServ.addActivityLog(
                        'user',
                        user.uid,
                        describeUserEmailChange(user.profile?.name || 'un usuario', data?.previousEmail, data?.email)
                    );
                } catch (logError) {
                    console.error('No se pudo registrar el cambio de correo', logError);
                }
            }
            return data?.email as string;
        } catch (error: any) {
            throw serverError(error);
        }
    };

    /**
     * Restablece el PIN de alguien que lo olvidó (el server lo cambia en Firebase
     * Auth). El gate va AQUÍ, no en el botón: quien lo administra (`canManageUser`),
     * y nunca el propio PIN — ése se cambia en Mi perfil, pidiendo el actual.
     */
    onResetPin = async (user: Pick<UserType, 'uid' | 'profile'>, pin: string): Promise<void> => {
        try {
            if (user.uid === UserServ.getUserId()) {
                throw new Error('Tu propio PIN se cambia en Mi perfil');
            }
            if (!canManageUser(getCurrentPermissionSubject(), user.profile)) {
                throw new Error('Este usuario no es de tus sucursales');
            }
            if (!/^\d{6}$/.test(pin)) throw new Error('El PIN debe ser de 6 dígitos');
            await axiosPrivate.post(`/users/${user.uid}/pin`, { pin });
            try {
                await ActivityLogServ.addActivityLog(
                    'user',
                    user.uid,
                    describeUserPinReset(user.profile?.name || 'un usuario')
                );
            } catch (logError) {
                console.error('No se pudo registrar el restablecimiento del PIN', logError);
            }
        } catch (error: any) {
            throw serverError(error);
        }
    };

    onLogin = (loginFormValues: LoginFormValues): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                await signInWithEmailAndPassword(auth, loginFormValues.email, loginFormValues.password);
                resolve();
            } catch (error) {
                reject(error);
            }
        });
    };

    onLogout = (): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                await signOut(auth);
                resolve();
            } catch (error) {
                reject(error);
            }
        });
    };

    getUsers = async (filters?: any, excludeCurrentAccount?: boolean): Promise<UserType[]> => {
        return new Promise(async (resolve, reject) => {
            try {
                const users = await UserServ.getUsers(filters, excludeCurrentAccount);
                resolve(users);
            } catch (error) {
                console.log(error);
                reject('Error getting user: ' + error);
            }
        });
    };

    getUsersByProfile = async (profile: ProfileTypes): Promise<UserType[]> => {
        return new Promise(async (resolve, reject) => {
            try {
                const users = await UserServ.getUsersByProfile(profile);
                resolve(users);
            } catch (error) {
                reject('Error getting users by profile: ' + error);
            }
        });
    };

    getUser = async (userId?: string): Promise<UserType> => {
        return new Promise(async (resolve, reject) => {
            try {
                const user = await UserServ.getUser(userId);
                resolve(user);
            } catch (error) {
                reject('Error getting user: ' + error);
            }
        });
    };

    getMainBranchOffice = async (userId?: string): Promise<BranchOffice> => {
        return new Promise(async (resolve, reject) => {
            try {
                const branchOffice = await UserServ.getMainBranchOffice(userId);
                resolve(branchOffice);
            } catch (error) {
                reject('Error getting branch office: ' + error);
            }
        });
    };

    uploadProfilePhoto = async (photoBase64: string): Promise<string> => {
        return new Promise(async (resolve, reject) => {
            try {
                const url = await UserServ.uploadProfilePhoto(photoBase64);
                resolve(url);
            } catch (error) {
                reject('Error updating profile photo: ' + error);
            }
        });
    };

    deleteUser = (userId: string): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                // Los dos admins borran, cada uno en su alcance (2026-09-30): el de
                // sucursal, solo a quien administra (`canManageUser`: perfil que él
                // asigna y todas sus sucursales dentro de las suyas). Nunca a sí mismo.
                if (userId === UserServ.getUserId()) {
                    reject('No puedes eliminar tu propio usuario');
                    return;
                }
                const target = await UserServ.getUser(userId);
                if (!canCurrentUser(Capability.UsersDelete) || !canManageUser(getCurrentPermissionSubject(), target.profile)) {
                    reject('Este usuario no es de tus sucursales');
                    return;
                }
                await axiosPrivate.delete(`/users/${userId}`);
                resolve();
            } catch (error: any) {
                reject(error.response.data.message);
            }
        });
    };
    toggleStatus = (userId: string): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                await UserServ.toggleStatus(userId);
                resolve();
            } catch (error) {
                reject(error);
            }
        });
    };

    updatePersonalData = (personalData: PersonalDataType): Promise<UserType> => {
        return new Promise(async (resolve, reject) => {
            try {
                const newUser = await UserServ.updatePersonalData(personalData);
                resolve(newUser);
            } catch (error) {
                reject(error);
            }
        });
    };

    updatePin = (updatePin: UpdatePinType): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                await UserServ.updatePin(updatePin);
                resolve();
            } catch (error) {
                reject(error);
            }
        });
    };
}

export default UserService;
